import { useEffect, useState } from 'preact/hooks'
import { useForm } from 'react-hook-form'
import { X, Building2, Plus, AlertTriangle } from 'lucide-preact'
import { clientCompanyApi } from '@/api'
import { setFormValidationErrors } from '@/utils/errorHandling'
import { useToast } from '@/hooks/useToast'
import { useLookups } from '@/hooks/useLookups'
import type { ClientCompanyCreateDto, ClientCompanyViewDto } from '@/types/company'
import { useAuth } from '@/auth/useAuth'
import { CompanyLookup } from '@/components/common/CompanyLookup'
import { SlugInput } from '@/components/common/SlugInput'
import { WorkspaceFormFields } from '@/components/settings/company/WorkspaceFormFields'
import { emptyNewClientWorkspaceForm, toNewClientWorkspace, type NewClientWorkspaceForm } from './newClientWorkspace'

interface AddCustomerModalProps {
    isOpen: boolean
    managedByCompanyId: string
    onClose: () => void
    onCreated: (company: ClientCompanyViewDto) => void
}

type CompanyForm = {
    Name: string
    Slug: string
    CompanyId: string
    CompanyUrl: string
    Status: string
}

export function AddCustomerModal({ isOpen, managedByCompanyId, onClose, onCreated }: AddCustomerModalProps) {
    const [loading, setLoading] = useState(false)
    const [step, setStep] = useState<1 | 2>(1)
    const [rootError, setRootError] = useState<string | null>(null)
    const [companyName, setCompanyName] = useState('')
    const [enrichLoading, setEnrichLoading] = useState(false)
    const { getStatusOptions } = useLookups()
    const { refreshUserProfile } = useAuth()

    const { showError } = useToast()

    const COMPANY_LIMITS = {
        NAME_MAX: 200,
        URL_MAX: 2083
    }

    const isValidHttpUrl = (value: string) => {
        try {
            const url = new URL(value)
            return (url.protocol === 'http:' || url.protocol === 'https:') && url.hostname.includes('.')
        } catch {
            return false
        }
    }

    const {
        register: registerCompany,
        handleSubmit: handleSubmitCompany,
        formState: { errors: companyErrors, isValid: isCompanyValid, isDirty: isCompanyDirty },
        reset: resetCompany,
        watch: watchCompany,
        setValue: setCompanyValue,
        setError: setCompanyError
    } = useForm<CompanyForm>({
        mode: 'onChange',
        defaultValues: {
            Name: '',
            Slug: '',
            CompanyId: '',
            CompanyUrl: '',
            Status: ''
        }
    })

    const {
        register: registerWorkspace,
        control: controlWorkspace,
        handleSubmit: handleSubmitWorkspace,
        formState: { errors: workspaceErrors, isValid: isWorkspaceValid, isDirty: isWorkspaceDirty },
        reset: resetWorkspace,
        setError: setWorkspaceError,
        watch: watchWorkspace,
        setValue: setWorkspaceValue
    } = useForm<NewClientWorkspaceForm>({
        mode: 'onChange',
        defaultValues: emptyNewClientWorkspaceForm()
    })

    useEffect(() => {
        if (isOpen) {
            setStep(1)
            setRootError(null)
            setLoading(false)
            setCompanyName('')
            setEnrichLoading(false)
            resetCompany()
            resetWorkspace(emptyNewClientWorkspaceForm())
        }
    }, [isOpen, resetCompany, resetWorkspace])

    if (!isOpen) return null

    const handleCreate = async (companyData: CompanyForm, workspaceData: NewClientWorkspaceForm) => {
        setLoading(true)
        setRootError(null)
        try {
            const payload: ClientCompanyCreateDto = {
                Name: companyData.Name.trim(),
                Slug: companyData.Slug.trim(),
                CompanyId: companyData.CompanyId.trim(),
                CompanyUrl: companyData.CompanyUrl.trim(),
                CompanyType: 'Customer',
                Status: companyData.Status.trim(),
                Workspace: toNewClientWorkspace(workspaceData),
            }

            const res = await clientCompanyApi.create(managedByCompanyId, payload)
            if (res.error) {
                // Global handler shows toasts for 422 validation errors
                // Set form field errors for 422 responses
                if (res.status === 422) {
                    // Try to set form-level errors first
                    if (!setFormValidationErrors(res, setCompanyError) &&
                        !setFormValidationErrors(res, setWorkspaceError)) {
                        // If no specific field errors, show a general error
                        setRootError('Please check the form for validation errors')
                    } else {
                        setRootError('Please correct the highlighted fields')
                    }
                } else {
                    // For non-422 errors, show error message
                    setRootError(res.error)
                }
                return
            }
            if (res.data) {
                // Refresh the user profile to pick up any new roles from the created customer
                try {
                    await refreshUserProfile()
                } catch (e) {
                    // Non-blocking; continue flow even if refresh fails
                    console.warn('Failed to refresh user profile after customer create:', e)
                }
                onCreated(res.data)
                onClose()
            }
        } catch (e: unknown) {
            const err = e as { message?: string }
            // Non-422 errors need manual toast
            showError('Create customer failed', err?.message || 'Failed to create customer')
            setRootError(err?.message || 'Failed to create customer')
        } finally {
            setLoading(false)
        }
    }

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/50 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-primary-100 dark:bg-primary-900/20 flex items-center justify-center">
                            <Building2 className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                        </div>
                        <div>
                            <h2 className="text-xl font-semibold text-slate-900 dark:text-white">New Customer</h2>
                            <p className="text-sm text-slate-600 dark:text-slate-300">Create a client company with its first workspace</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-6">
                    {rootError && (
                        <div className="mb-4 flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400">
                            <AlertTriangle className="w-4 h-4" />
                            <span className="text-sm">{rootError}</span>
                        </div>
                    )}

                    {step === 1 && (
                        <form id="customer-company-form" className="space-y-6" onSubmit={handleSubmitCompany(() => setStep(2))}>
                            <div className="grid grid-cols-1 gap-6">
                                <div className="space-y-4">
                                    <div>
                                        <CompanyLookup
                                            companyId={watchCompany('CompanyId')}
                                            companyName={companyName}
                                            onChange={(id, name) => {
                                                // Only set CompanyId when we have both id and name (from fetch or search)
                                                if (id && name && name.trim().length > 0) {
                                                    setCompanyValue('CompanyId', id, { shouldValidate: true, shouldDirty: true })
                                                    setCompanyValue('Name', name, { shouldValidate: true, shouldDirty: true })
                                                    setCompanyName(name)
                                                }
                                            }}
                                            onLoadingChange={setEnrichLoading}
                                            error={companyErrors.CompanyId?.message}
                                            disabled={loading || enrichLoading}
                                            required={true}
                                        />
                                        {/* Hidden field for CompanyId validation */}
                                        <input
                                            type="hidden"
                                            {...registerCompany('CompanyId', {
                                                required: 'Company is required. Please click "Fetch from LinkedIn" or select a company from search results.',
                                                validate: v => {
                                                    if (!v || v.trim().length === 0) {
                                                        return 'Company is required. Please click "Fetch from LinkedIn" or select a company from search results.'
                                                    }
                                                    return true
                                                }
                                            })}
                                        />
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                        <div className="md:col-span-3">
                                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Company Name <span className="text-red-500">*</span></label>
                                            <input
                                                type="text"
                                                {...registerCompany('Name', {
                                                    required: 'Company name is required',
                                                    maxLength: { value: COMPANY_LIMITS.NAME_MAX, message: `Company name must be ${COMPANY_LIMITS.NAME_MAX} characters or less` },
                                                    validate: v => !!v?.trim() || 'Name cannot be just whitespace'
                                                })}
                                                className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 ${companyErrors.Name ? 'border-red-300 dark:border-red-600 focus:ring-red-500' : 'border-slate-300 dark:border-slate-600 focus:ring-primary-500'}`}
                                                placeholder="Enter company name"
                                            />
                                            {companyErrors.Name && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{companyErrors.Name.message}</p>}
                                            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">This will be auto-filled when you fetch a company, but you can customize it</p>
                                        </div>
                                        <div className="md:col-span-3">
                                            <SlugInput
                                                label="Slug"
                                                required
                                                value={watchCompany('Slug') || ''}
                                                onChange={(v) => setCompanyValue('Slug', v, { shouldValidate: true, shouldDirty: true })}
                                                sourceName={watchCompany('Name')}
                                                error={companyErrors.Slug?.message as string | undefined}
                                                checkAvailability={async (slug) => {
                                                    const res = await clientCompanyApi.checkSlug(slug)
                                                    return res.data ?? { available: false }
                                                }}
                                                helperText="This becomes the subdomain for this company's deployed landing pages (e.g. nike.demandwebs.com)."
                                            />
                                            {/* Hidden registration so react-hook-form tracks it for validation/submission */}
                                            <input type="hidden" {...registerCompany('Slug', {
                                                required: 'Slug is required',
                                                pattern: { value: /^[a-z0-9]+(?:-[a-z0-9]+)*$/, message: 'Slug must be lowercase letters, numbers, and single hyphens.' },
                                                minLength: { value: 3, message: 'Slug must be at least 3 characters.' },
                                                maxLength: { value: 50, message: 'Slug must be 50 characters or fewer.' },
                                            })} />
                                        </div>
                                        <div className="md:col-span-1">
                                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Status <span className="text-red-500">*</span></label>
                                            <select
                                                {...registerCompany('Status', { required: 'Status is required' })}
                                                className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 ${companyErrors.Status ? 'border-red-300 dark:border-red-600 focus:ring-red-500' : 'border-slate-300 dark:border-slate-600 focus:ring-primary-500'}`}
                                            >
                                                <option value="">Select status…</option>
                                                {getStatusOptions().map(opt => (
                                                    <option key={opt.Key} value={opt.Key}>{opt.Description}</option>
                                                ))}
                                            </select>
                                            {companyErrors.Status && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{companyErrors.Status.message}</p>}
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Website <span className="text-red-500">*</span></label>
                                        <input
                                            type="url"
                                            {...registerCompany('CompanyUrl', {
                                                required: 'Website is required',
                                                maxLength: { value: COMPANY_LIMITS.URL_MAX, message: 'Website URL too long' },
                                                validate: value => isValidHttpUrl(value) || 'Enter a valid http(s) URL with a domain'
                                            })}
                                            className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 ${companyErrors.CompanyUrl ? 'border-red-300 dark:border-red-600 focus:ring-red-500' : 'border-slate-300 dark:border-slate-600 focus:ring-primary-500'}`}
                                            placeholder="https://example.com"
                                        />
                                        {companyErrors.CompanyUrl && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{companyErrors.CompanyUrl.message}</p>}
                                    </div>
                                </div>

                            </div>
                        </form>
                    )}

                    {step === 2 && (
                        <form id="customer-workspace-form" className="space-y-6" onSubmit={handleSubmitWorkspace(async (workspace) => {
                            const companyData = watchCompany()
                            await handleCreate(companyData, workspace)
                        })}>
                            <WorkspaceFormFields control={controlWorkspace} register={registerWorkspace} errors={workspaceErrors} watch={watchWorkspace} setValue={setWorkspaceValue} />
                        </form>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between gap-3 p-6 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-700/50">
                    <div className="text-sm text-slate-500 dark:text-slate-300">{step === 1 ? 'Step 1 of 2: Company' : 'Step 2 of 2: First Workspace'}</div>
                    <div className="flex items-center gap-3">
                        <button type="button" onClick={onClose} className="px-4 py-2 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-600 rounded-lg transition-colors">Cancel</button>
                        {step === 2 && (
                            <button type="button" onClick={() => setStep(1)} className="px-4 py-2 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-600 rounded-lg transition-colors">Back</button>
                        )}
                        {step === 1 ? (
                            <button
                                type="submit"
                                form="customer-company-form"
                                disabled={!isCompanyValid || !isCompanyDirty || loading}
                                className={`px-4 py-2 rounded-lg text-white ${(!isCompanyValid || !isCompanyDirty || loading) ? 'bg-slate-300 dark:bg-slate-600' : 'bg-primary-600 hover:bg-primary-700'} transition-colors`}
                            >
                                Next
                            </button>
                        ) : (
                            <button
                                type="submit"
                                form="customer-workspace-form"
                                disabled={!isWorkspaceValid || !isWorkspaceDirty || loading}
                                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white ${(!isWorkspaceValid || !isWorkspaceDirty || loading) ? 'bg-slate-300 dark:bg-slate-600' : 'bg-primary-600 hover:bg-primary-700'} transition-colors`}
                            >
                                <Plus className="w-4 h-4" />
                                {loading ? 'Creating…' : 'Create Customer'}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}
